import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReduxState } from "..";
import { getXeroAccounts, createXeroAccount } from "../../docuclipper/api";

export interface XeroAccount {
  name: string;
  value: string;
  code?: string;
  type?: string;
}

const initialState: {
  xeroAccounts: XeroAccount[];
  loading: boolean;
  creating: boolean;
  error: string | null;
} = {
  xeroAccounts: [],
  loading: false,
  creating: false,
  error: null,
};

const slice = createSlice({
  name: "XeroAccount",
  initialState,
  reducers: {
    load(state) {
      state.loading = true;
      state.error = null;
    },
    setData(state, action: PayloadAction<XeroAccount[]>) {
      state.loading = false;
      state.xeroAccounts = action.payload;
    },
    setError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    setCreating(state, action: PayloadAction<boolean>) {
      state.creating = action.payload;
    },
    addXeroAccount(state, action: PayloadAction<XeroAccount>) {
      state.xeroAccounts.unshift(action.payload);
      state.creating = false;
    },
  },
});

export const { load, setError, setData, setCreating, addXeroAccount } = slice.actions;

export const fetchXeroAccounts = (integrationId: number, search?: string) => async (dispatch, getState) => {
  const state: ReduxState = getState();
  if (state.XeroAccount.loading) {
    return;
  }
  dispatch(load());
  try {
    const accounts = await getXeroAccounts(integrationId, search);
    dispatch(
      setData(
        (accounts || []).map((a: any) => ({
          // Xero API returns lowercase field names
          name: a.name || a.Name,
          value: a.accountID || a.AccountID,
          code: a.code || a.Code,
          type: a.type || a.Type,
        }))
      )
    );
  } catch (err) {
    dispatch(setError((err as any).message || "Error getting Xero accounts"));
  }
};

export const createAndAddXeroAccount = (integrationId: number, accountName: string) => async (dispatch, getState) => {
  dispatch(setCreating(true));
  try {
    const account = await createXeroAccount(integrationId, accountName);
    const newAccount: XeroAccount = {
      name: account.name || account.Name,
      value: account.accountID || account.AccountID,
      code: account.code || account.Code,
      type: account.type || account.Type,
    };
    dispatch(addXeroAccount(newAccount));
    return newAccount;
  } catch (err) {
    dispatch(setError((err as any).message || "Error creating Xero account"));
    throw err;
  } finally {
    dispatch(setCreating(false));
  }
};

export default slice.reducer;

